import { isPendingChatMessageId } from "@/domains/offline/chat/chatOutboxStore";

export type ThreadMessage = {
  id: string;
  senderId: string;
  content: string;
  createdAt: string;
  conversationId?: string;
  pending?: boolean;
};

export function sortThreadMessages(msgs: ThreadMessage[]): ThreadMessage[] {
  return [...msgs].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

const DEDUPE_WINDOW_MS = 120_000;

export function isNearDuplicate(a: ThreadMessage, b: ThreadMessage): boolean {
  if (a.senderId !== b.senderId) {
    return false;
  }

  if (a.content.trim() !== b.content.trim()) {
    return false;
  }

  const delta = Math.abs(new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  return delta <= DEDUPE_WINDOW_MS;
}

function isPendingMessage(m: ThreadMessage): boolean {
  return Boolean(m.pending) || isPendingChatMessageId(m.id);
}

function dedupeByFingerprint(messages: ThreadMessage[]): ThreadMessage[] {
  const sorted = sortThreadMessages(messages);
  const kept: ThreadMessage[] = [];

  for (const m of sorted) {
    const duplicateIndex = kept.findIndex((existing) => isNearDuplicate(existing, m));
    if (duplicateIndex === -1) {
      kept.push(m);
      continue;
    }

    const existing = kept[duplicateIndex];
    if (isPendingMessage(existing) && !isPendingMessage(m)) {
      kept[duplicateIndex] = m;
    }
  }

  return kept;
}

function dropPendingMatchingConfirmed(messages: ThreadMessage[]): ThreadMessage[] {
  const confirmed = messages.filter((m) => !isPendingMessage(m));

  return messages.filter((m) => {
    if (!isPendingMessage(m)) {
      return true;
    }

    return !confirmed.some((s) => isNearDuplicate(s, m));
  });
}

/** Collapse duplicate bubbles (pending+server or double-synced server copies). */
export function dedupeThreadMessages(messages: ThreadMessage[]): ThreadMessage[] {
  const fingerprintDeduped = dedupeByFingerprint(messages);
  const withoutPendingDupes = dropPendingMatchingConfirmed(fingerprintDeduped);
  return sortThreadMessages(withoutPendingDupes);
}
